import { Dispatch, ReactNode, SetStateAction, useCallback } from "react";
import { Link, generatePath } from "react-router-dom";
import moment from "moment";
import TableContainer from "@mui/material/TableContainer";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import { formatDashDate, formatName } from "helpers/CommonHelper";
import { ITableColumn } from "interfaces/Common";
import TableSortLabel from "components/TableSortLabel/TableSortLabel";
import { URLRoutes } from "enums/Routing";
import { OrderCancelType, OrderStatus } from "enums/Order";
import {
  IPatientProgress,
  IPatientProgressFilters,
} from "interfaces/Patient";
import useFetchTableData from "hooks/useFetchTableData";
import useSnackbar from "hooks/useSnackbar";
import PatientService from "services/PatientService";
import Loading from "components/Loading/Loading";
import { formatPhoneNumber, handleShortlistPatient } from "helpers/PatientHelper";
import NoTableItems from "components/Tables/NoTableItems/NoTableItems";
import tableStyles from "styles/shared/table.module.scss";
import styles from "./PatientProgressTable.module.scss";

interface PatientProgressTableProps {
  filters: IPatientProgressFilters;
  setFilters: Dispatch<SetStateAction<IPatientProgressFilters>>;
}

const tableColumns: ITableColumn[] = [
  { label: "Name", sortProperty: "last_name" },
  { label: "Date of Birth" },
  { label: "Phone" },
  { label: "% Change", sortProperty: "balance_trend" },
  { label: "Score - Date" },
  { label: "% Change", sortProperty: "gait_trend" },
  { label: "Score - Date" },
  { label: "Days Used", sortProperty: "days_used" },
  { label: "Ordering Clinician", sortProperty: "ordering_clinician" },
  { label: "Order Status", sortProperty: "order_end_date" },
];

const formatStatusCellContent = ({
  status,
  end,
  cancelType,
}: {
  status: OrderStatus;
  end: string;
  cancelType?: OrderCancelType | null;
}): ReactNode => {
  const isExpired = moment().isSameOrAfter(end);
  switch (true) {
    case cancelType === OrderCancelType.VoidRequested:
      return "Void Requested";
    case cancelType === OrderCancelType.Voided:
      return "Voided";
    case status === OrderStatus.Cancelled:
      return "Canceled";
    case isExpired:
      return "Expired";
    default:
      return (
        <>
          Expires
          <br />
          {formatDashDate(end)}
        </>
      );
  }
};

const PatientProgressTable = (props: PatientProgressTableProps) => {
  const { filters, setFilters } = props;
  const { clinicianId, _order_by: orderBy } = filters;

  const { snackbarComponent, setSnackbarError, setSnackbarSuccess } =
    useSnackbar();

  const onPageIncrement = useCallback(
    () => setFilters((prev) => ({ ...prev, page: prev.page + 1 })),
    [setFilters]
  );

  const { data, setData, loading, lastElementRef } = useFetchTableData<
    IPatientProgress,
    IPatientProgressFilters
  >({
    filters,
    onPageIncrement,
    fetchAPI: PatientService.getPatientProgress,
    onError: setSnackbarError,
  });

  const renderTrend = (trend: number | null): ReactNode => {
    if (trend === null) return <div className="text-center text-fiord">-</div>;

    let bgColorClass: string;
    let textColorClass: string;
    let iconClass: string;

    switch (true) {
      case trend === 0:
        bgColorClass = "bg-fiord";
        textColorClass = "text-fiord";
        iconClass = "icon_minus_2";
        break;
      case trend > 0:
        bgColorClass = "bg-tropicalRainForest";
        textColorClass = "text-tropicalRainForest";
        iconClass = "icon_arrow_up";
        break;
      default:
        bgColorClass = "bg-crimson";
        textColorClass = "text-crimson";
        iconClass = "icon_arrow_down";
    }

    return (
      <div className={`flex items-center gap-1 text-sm ${textColorClass}`}>
        <div className={`flex items-center justify-center rounded-full ${bgColorClass} w-5 h-5`}>
          <span className={`icon_svg ${iconClass}`}></span>
        </div>
        {`${trend > 0 ? "+" : ""}${trend}%`}
      </div>
    );
  };

  const renderScoreDates = ({
    firstScore,
    firstDate,
    lastScore,
    lastDate,
  }: {
    firstScore: number | null;
    firstDate: string | null;
    lastScore: number | null;
    lastDate: string | null;
  }): ReactNode => {
    if (firstScore === null) return <div className="text-center text-fiord">-</div>;

    // Only 1 test day
    if (firstDate === lastDate) {
      return (
        <div className="text-xs whitespace-nowrap">
          <div className="flex gap-1 items-baseline"><span className="font-bold text-sm" style={{width: "24px"}}>{firstScore}</span><span>-</span><span>{firstDate && formatDashDate(firstDate)}</span></div>
        </div>
      );
    }

    // 2+ test days — newest on top, oldest on bottom
    return (
      <div className="flex flex-col gap-0.5 text-xs whitespace-nowrap">
        <div className="flex gap-1 items-baseline"><span className="font-bold text-sm" style={{width: "24px"}}>{lastScore}</span><span>-</span><span>{formatDashDate(lastDate!)}</span></div>
        <div className="flex gap-1 items-baseline"><span className="font-bold text-sm" style={{width: "24px"}}>{firstScore}</span><span>-</span><span>{formatDashDate(firstDate!)}</span></div>
      </div>
    );
  };

  return (
    <>
      <div className="relative grow">
        <TableContainer
          className={`${tableStyles.shared_table_container} ${tableStyles.padding_20} ${styles.patient_progress_table}`}
          style={{ maxHeight: "none", overflow: "visible" }}
        >
          <Table>
            <TableHead>
              {/* Row 1: spanning RKM header */}
              <TableRow>
                <TableCell colSpan={3} />
                <TableCell
                  colSpan={2}
                  className="!text-center !font-bold"
                >
                  Balance
                </TableCell>
                <TableCell
                  colSpan={2}
                  className="!text-center !font-bold"
                >
                  Gait
                </TableCell>
                <TableCell
                  colSpan={3}
                  className="!text-center !bg-[#94B6CE] !font-bold"
                >
                  Remote Kinematic Monitoring (RKM)
                </TableCell>
                <TableCell className="italic !text-xs !font-normal !whitespace-nowrap !leading-tight !w-24">
                  Click on the header<br />to sort by column
                </TableCell>
              </TableRow>
              {/* Row 2: column headers */}
              <TableRow>
                {tableColumns.map(({ label, sortProperty }, index) => {
                  const isRKM = index >= 7 && index <= 9;
                  return (
                    <TableCell
                      key={`${label}-${index}`}
                      className={isRKM ? "!bg-[#87A9C1]" : ""}
                    >
                      {sortProperty ? (
                        <TableSortLabel
                          label={label}
                          sortProperty={sortProperty}
                          orderBy={orderBy}
                          onSort={(value) =>
                            setFilters((prev) => ({
                              ...prev,
                              page: 1,
                              _order_by: value,
                            }))
                          }
                        />
                      ) : (
                        label
                      )}
                    </TableCell>
                  );
                })}
                {/* Empty actions header */}
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map(
                (
                  {
                    id,
                    firstName,
                    lastName,
                    middleName,
                    dob,
                    phone,
                    balanceTrend,
                    balanceFirstScore,
                    balanceFirstDate,
                    balanceLastScore,
                    balanceLastDate,
                    gaitTrend,
                    gaitFirstScore,
                    gaitFirstDate,
                    gaitLastScore,
                    gaitLastDate,
                    isShortlist,
                    daysUsed,
                    orderingClinician,
                    orderStatus,
                    orderEndDate,
                    orderCancelType,
                  },
                  index
                ) => (
                  <TableRow
                    key={id}
                    ref={index === data.length - 1 ? lastElementRef : null}
                  >
                    <TableCell className="!font-bold">
                      {formatName({ lastName, firstName, middleName })}
                    </TableCell>
                    <TableCell>{formatDashDate(dob)}</TableCell>
                    <TableCell>{phone ? formatPhoneNumber(phone) : "-"}</TableCell>
                    <TableCell className="!bg-pattensBlue2/50">
                      {renderTrend(balanceTrend)}
                    </TableCell>
                    <TableCell className="!bg-pattensBlue2/50">
                      {renderScoreDates({
                        firstScore: balanceFirstScore,
                        firstDate: balanceFirstDate,
                        lastScore: balanceLastScore,
                        lastDate: balanceLastDate,
                      })}
                    </TableCell>
                    <TableCell>
                      {renderTrend(gaitTrend)}
                    </TableCell>
                    <TableCell>
                      {renderScoreDates({
                        firstScore: gaitFirstScore,
                        firstDate: gaitFirstDate,
                        lastScore: gaitLastScore,
                        lastDate: gaitLastDate,
                      })}
                    </TableCell>
                    <TableCell className="!bg-malibuLight !px-2 !text-center">
                      {daysUsed ?? "-"}
                    </TableCell>
                    <TableCell className="!bg-malibuLight">
                      {orderingClinician ?? "-"}
                    </TableCell>
                    <TableCell className="!bg-malibuLight">
                      {orderStatus && orderEndDate
                        ? formatStatusCellContent({
                            status: orderStatus,
                            end: orderEndDate,
                            cancelType: orderCancelType,
                          })
                        : "-"}
                    </TableCell>
                    <TableCell className="!px-2 !text-right">
                      <div className="flex justify-end items-center gap-3">
                        <Link
                          className="text-pelorous hover:underline"
                          to={
                            clinicianId
                              ? generatePath(
                                  URLRoutes.ClinicianPatientDashboard,
                                  {
                                    clinicianId,
                                    patientId: id,
                                  }
                                )
                              : generatePath(URLRoutes.PatientDashboard, {
                                  patientId: id,
                                })
                          }
                        >
                          View Patient
                        </Link>

                        <button
                          className="flex"
                          onClick={() =>
                            handleShortlistPatient<IPatientProgress>({
                              id,
                              isShortlist,
                              setData,
                              onSuccess: setSnackbarSuccess,
                              onError: setSnackbarError,
                            })
                          }
                        >
                          <span
                            className={`icon_svg ${
                              isShortlist ? "icon_star_filled" : "icon_star"
                            }`}
                          />
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                )
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {!loading && !data.length && (
          <div className="mt-7">
            <NoTableItems
              icon="icon_no_results"
              message={"No results for selected filters"}
            />
          </div>
        )}

        <Loading loading={loading} />
      </div>

      {snackbarComponent}
    </>
  );
};

export default PatientProgressTable;
